export const en = {
  nav: {
    groups: {
      overview: 'Overview',
      practice: 'Practice',
      clinical: 'Clinical',
      finance: 'Billing & Finance',
      communication: 'Communication',
      team: 'Team & HR',
      admin: 'Administration',
    },
    dashboard: 'Dashboard',
    appointments: 'Appointments',
    patients: 'Patients',
    enquiries: 'Enquiries',
    onlineBookings: 'Online Bookings',
    consultations: 'Consultations',
    treatmentPlans: 'Treatment Plans',
    prescriptions: 'Prescriptions',
    exercises: 'Exercise Library',
    billing: 'Billing & Payments',
    packages: 'Packages',
    paymentReminders: 'Payment Reminders',
    reports: 'Reports & Analytics',
    messages: 'Messages',
    staff: 'Clinic Members',
    attendance: 'Attendance',
    leave: 'Leave',
    payroll: 'Payroll',
    branches: 'Branches',
    settings: 'Settings',
    audit: 'Audit Log',
    support: 'Help & Support',
    patientPortal: 'Patient Portal',
    collapse: 'Collapse section',
    expand: 'Expand section',
    profile: 'My profile',
    allBranches: 'All branches',
    branch: 'Branch',
    mainNav: 'Main navigation',
    openMenu: 'Open menu',
    skipToContent: 'Skip to content',
    searchPlaceholder: 'Search patients by name, phone or MRN…',
  },
  auth: {
    firstLogin: { title: 'Set your password', welcome: 'Welcome, {{name}}', body: 'Your administrator created a temporary password for you. Choose your own password to continue. It also works in the Physio360 mobile app.', current: 'Temporary password', submit: 'Save and continue', done: 'Password saved. Welcome aboard!' },
    title: 'Sign in to your clinic',
    subtitle: 'Manage · Treat · Track · Grow',
    email: 'Email',
    password: 'Password',
    remember: 'Keep me signed in on this device',
    forgot: 'Forgot password?',
    submit: 'Sign in',
    forgotTitle: 'Reset your password',
    forgotHelp: 'Enter your work email and we will send you a reset link.',
    sendLink: 'Send reset link',
    backToSignIn: 'Back to sign in',
    resetTitle: 'Choose a new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    resetSubmit: 'Update password',
    heroTitle: 'Complete physiotherapy management for a healthier tomorrow',
    heroText: 'Appointments, patient records, treatment plans, billing and analytics — in one secure platform.',
    tagline: 'Move Better. Live Stronger.',
  },
  dashboard: {
    greeting: { morning: 'Good morning, {{name}}!', afternoon: 'Good afternoon, {{name}}!', evening: 'Good evening, {{name}}!' },
    subtitle: "Here's what's happening at your clinic today.",
    todayAppointments: "Today's Appointments",
    activePatients: 'Active Patients',
    revenue: 'Revenue',
    pendingPayments: 'Pending Payments',
    fromYesterday: 'from yesterday',
    thisMonth: 'this month',
    vsLastMonth: 'vs last month',
    viewDetails: 'View details',
    upcoming: "Today's Schedule",
    noAppointmentsToday: 'No appointments today',
    patientProgress: 'Patient Progress',
    patientsImproved: 'Patients improved',
    improvedHint: 'Pain reduced by 2+ points (NPRS) since first visit',
    revenueTrend: 'Collections (last 14 days)',
    quickActions: 'Quick actions',
    newAppointment: 'New appointment',
    newPatient: 'New patient',
    newEnquiry: 'New enquiry',
    newInvoice: 'New invoice',
    followUpsDue: 'Follow-ups due',
    week: 'Week {{n}}',
  },
  appointments: {
    title: 'Appointment Management',
    today: 'Today',
    day: 'Day',
    week: 'Week',
    list: 'List',
    new: 'New Appointment',
    edit: 'Edit appointment',
    reschedule: 'Reschedule',
    patient: 'Patient',
    practitioner: 'Physiotherapist',
    allPractitioners: 'All physiotherapists',
    service: 'Service',
    room: 'Room',
    noRoom: 'No room',
    date: 'Date',
    time: 'Start time',
    duration: 'Duration (minutes)',
    type: 'Visit type',
    homeAddress: 'Home visit address',
    travelBuffer: 'Travel buffer (minutes)',
    notes: 'Notes',
    repeat: 'Repeat weekly',
    repeatOn: 'Repeat on',
    occurrences: 'Number of visits',
    skipConflicts: 'Skip dates that conflict instead of cancelling the whole series',
    outsideHours: 'Allow booking outside working hours',
    book: 'Book appointment',
    booked: 'Appointment booked',
    seriesBooked: '{{count}} appointments booked',
    seriesSkipped: '{{count}} dates skipped because of conflicts',
    updated: 'Appointment updated',
    conflictTitle: 'That time is not available',
    conflictWith: {
      practitioner: 'The physiotherapist already has an appointment at {{time}}',
      room: 'The room is booked at {{time}}',
      patient: 'The patient already has an appointment at {{time}}',
    },
    freeSlots: 'Free slots',
    noSlots: 'No free slots on this day',
    details: 'Appointment details',
    actions: {
      confirm: 'Confirm',
      checkIn: 'Check in',
      start: 'Start session',
      complete: 'Complete',
      cancel: 'Cancel appointment',
      noShow: 'Mark no-show',
      invoice: 'Create invoice',
      openPatient: 'Open patient record',
      writeNote: 'Write note',
    },
    cancelReason: 'Reason for cancellation',
    cancelConfirm: 'Cancel this appointment?',
    noShowConfirm: 'Mark the patient as a no-show?',
    statusUpdated: 'Status updated',
    bookedVia: { staff: 'Booked by staff', portal: 'Booked in patient app' },
    empty: 'No appointments in this period',
    week_of: 'Week of {{date}}',
    now: 'Now',
    closed: 'Not working',
  },
  patients: {
    photo: { change: 'Change photo', take: 'Take photo', upload: 'Upload photo', remove: 'Remove photo', saved: 'Photo updated' },
    title: 'Patients',
    subtitle: 'Search, register and manage patient records.',
    new: 'New patient',
    edit: 'Edit patient',
    search: 'Search by name, phone, email or MRN',
    mrn: 'MRN',
    name: 'Name',
    firstName: 'First name',
    lastName: 'Last name',
    dob: 'Date of birth',
    age: 'Age',
    years: '{{count}} years',
    gender: 'Gender',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    city: 'City',
    occupation: 'Occupation',
    referredBy: 'Referred by',
    emergencyName: 'Emergency contact',
    emergencyPhone: 'Emergency phone',
    medicalHistory: 'Medical history',
    allergies: 'Allergies',
    notes: 'Notes',
    language: 'Preferred language',
    branch: 'Registered branch',
    primaryPractitioner: 'Primary physiotherapist',
    status: 'Status',
    lastVisit: 'Last visit',
    nextVisit: 'Next visit',
    created: 'Patient registered',
    updated: 'Patient updated',
    archived: 'Patient archived',
    archive: 'Archive patient',
    archiveConfirm: 'Archive this patient? Their history is kept and can be restored by support.',
    empty: 'No patients found',
    emptyHint: 'Register your first patient to get started.',
    sections: { personal: 'Personal information', contact: 'Contact', clinical: 'Clinical background', care: 'Care team' },
    tabs: {
      overview: 'Overview',
      timeline: 'Timeline',
      appointments: 'Appointments',
      assessments: 'Assessments',
      notes: 'Progress Notes',
      plans: 'Treatment Plan',
      prescriptions: 'Prescriptions',
      progress: 'Progress',
      documents: 'Documents',
      billing: 'Invoices & Payments',
    },
    portal: {
      title: 'Patient app',
      enabled: 'Patient app access is active',
      disabled: 'Not invited to the patient app yet',
      invite: 'Invite to patient app',
      reinvite: 'Send new invite',
      disable: 'Disable access',
      linkReady: 'Activation link (valid 72 hours)',
      copy: 'Copy link',
      copied: 'Link copied',
    },
    insurance: { title: 'Insurance', add: 'Add policy', payer: 'Insurer / TPA', policy: 'Policy number', member: 'Member ID', coverage: 'Coverage %', validTo: 'Valid until', none: 'No insurance on file' },
  },
  clinical: {
    newNote: 'New progress note',
    soap: { subjective: 'Subjective', objective: 'Objective', assessment: 'Assessment', plan: 'Plan' },
    soapHints: {
      subjective: 'What the patient reports: pain, function, response to last session',
      objective: 'Measurements and observations: ROM, strength, special tests',
      assessment: 'Your clinical interpretation and progress',
      plan: 'Treatment given and next steps',
    },
    painScore: 'Pain (0–10)',
    saveDraft: 'Save draft',
    saveAndSign: 'Save & sign',
    sign: 'Sign note',
    signConfirm: 'Sign this note? Signed notes become permanent and can only be corrected with an addendum.',
    signed: 'Signed {{date}} by {{name}}',
    draft: 'Draft',
    addendum: 'Add addendum',
    addendumPlaceholder: 'Correction or additional information…',
    addenda: 'Addenda',
    noNotes: 'No progress notes yet',
    noteSaved: 'Note saved',
    noteSigned: 'Note signed',
    newAssessment: 'New assessment',
    chiefComplaint: 'Chief complaint',
    history: 'History of present condition',
    painLocation: 'Pain location',
    diagnosis: 'Clinical diagnosis',
    goals: 'Goals',
    observations: 'Observations',
    rom: 'Range of motion',
    joint: 'Joint',
    movement: 'Movement',
    degrees: 'Degrees',
    strength: 'Muscle strength',
    muscle: 'Muscle',
    grade: 'Grade',
    specialTests: 'Special tests',
    testName: 'Test',
    result: 'Result',
    results: { positive: 'Positive', negative: 'Negative', inconclusive: 'Inconclusive' },
    addRow: 'Add row',
    noAssessments: 'No assessments yet',
    assessmentSaved: 'Assessment saved',
    progressTitle: 'Pain & function over time',
    recordProgress: 'Record outcome',
    functionScore: 'Function (0–100)',
    measure: 'Measure',
    value: 'Value',
    unit: 'Unit',
    noProgress: 'No outcome measures recorded yet',
    documents: 'Documents',
    upload: 'Upload file',
    category: 'Category',
    categories: { report: 'Report', imaging: 'Imaging', referral: 'Referral', prescription: 'Prescription', insurance: 'Insurance', consent: 'Consent', other: 'Other' },
    uploaded: 'File uploaded',
    noDocuments: 'No documents uploaded',
    maxSize: 'PDF, image or Word document up to 20 MB',
    consents: 'Consent forms',
    signConsent: 'Sign consent',
    signerName: 'Signer name',
    relationship: 'Relationship to patient',
    relationships: { self: 'Self', parent: 'Parent', guardian: 'Guardian', spouse: 'Spouse', other: 'Other' },
    signatureLabel: 'Signature',
    signatureHint: 'Ask the patient to sign with a finger or stylus.',
    consentSigned: 'Consent recorded',
    pendingConsents: 'Awaiting signature',
    witness: 'Witness: {{name}}',
  },
  plans: {
    title: 'Treatment Plans',
    subtitle: 'Active programmes and home exercise prescriptions.',
    new: 'New treatment plan',
    planTitle: 'Programme title',
    diagnosis: 'Diagnosis',
    goals: 'Goals',
    startDate: 'Start date',
    durationWeeks: 'Duration (weeks)',
    frequency: 'Sessions per week',
    status: 'Status',
    statuses: { draft: 'Draft', active: 'Active', completed: 'Completed', cancelled: 'Cancelled' },
    weekOf: 'Week {{current}} of {{total}}',
    exercises: 'Exercises ({{count}})',
    addExercise: 'Add exercise',
    printHep: 'Print exercise prescription',
    sets: 'Sets',
    reps: 'Reps',
    hold: 'Hold (s)',
    frequencyLabel: 'Frequency',
    exerciseNotes: 'Notes for patient',
    remove: 'Remove',
    noExercises: 'No exercises prescribed yet',
    noPlans: 'No treatment plans yet',
    created: 'Treatment plan created',
    saved: 'Plan updated',
    adherence: 'Home adherence (last 4 weeks)',
    adherenceHint: 'Days the patient ticked off exercises in the app',
    duration: 'Duration',
    weeks: '{{count}} weeks',
  },
  exercises: {
    title: 'Exercise Library',
    subtitle: 'Build home exercise programmes from your clinic library.',
    new: 'New exercise',
    edit: 'Edit exercise',
    name: 'Name',
    region: 'Body region',
    category: 'Category',
    difficulty: 'Difficulty',
    description: 'Short description',
    instructions: 'Instructions',
    defaults: 'Default dosage',
    videoUrl: 'Video link (https)',
    allRegions: 'All regions',
    allCategories: 'All categories',
    regions: { knee: 'Knee', shoulder: 'Shoulder', neck: 'Neck', back: 'Back', hip: 'Hip', ankle: 'Ankle', wrist: 'Wrist', elbow: 'Elbow', core: 'Core', general: 'General' },
    categories: { strengthening: 'Strengthening', flexibility: 'Flexibility', mobility: 'Mobility', stability: 'Stability', balance: 'Balance', posture: 'Posture', cardio: 'Cardio', functional: 'Functional' },
    difficulties: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
    saved: 'Exercise saved',
    empty: 'No exercises match your filters',
  },
  enquiries: {
    title: 'Enquiries',
    subtitle: 'Capture leads, follow up and convert them into patients.',
    new: 'New enquiry',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    source: 'Source',
    sources: { walk_in: 'Walk-in', phone: 'Phone call', website: 'Website', whatsapp: 'WhatsApp', referral: 'Referral', social: 'Social media', other: 'Other' },
    interest: 'Condition / reason',
    service: 'Interested in',
    message: 'Message',
    preferredTime: 'Best time to call',
    assignedTo: 'Assigned to',
    unassigned: 'Unassigned',
    branch: 'Branch',
    nextFollowUp: 'Next follow-up',
    lastContacted: 'Last contacted',
    received: 'Received',
    status: 'Status',
    filters: { open: 'Open', due: 'Due today', all: 'All' },
    summary: { open: 'Open enquiries', due: 'Follow-ups due', received: 'Received (30 days)', conversion: 'Conversion (30 days)' },
    logTitle: 'Log interaction',
    activityTypes: { call: 'Call', whatsapp: 'WhatsApp', email: 'Email', sms: 'SMS', visit: 'Visit', note: 'Note', status_change: 'Status changed', follow_up: 'Follow-up scheduled', converted: 'Converted' },
    outcome: 'What happened?',
    scheduleFollowUp: 'Schedule next follow-up',
    log: 'Save activity',
    logged: 'Activity saved',
    timeline: 'Timeline',
    convert: 'Convert to patient',
    convertTitle: 'Register as patient',
    convertHelp: 'Creates a patient record using the enquiry details. You can then book the first appointment.',
    convertExisting: 'Link to existing patient',
    duplicateWarning: 'A patient with this phone number already exists.',
    converted: 'Enquiry converted to patient',
    viewPatient: 'View patient',
    bookFirst: 'Book first appointment',
    markLost: 'Mark as lost',
    lostReason: 'Why was it lost?',
    reopen: 'Reopen',
    created: 'Enquiry captured',
    saved: 'Enquiry updated',
    empty: 'No enquiries here',
    emptyHint: 'Capture walk-ins, calls and WhatsApp messages so no lead is forgotten.',
    overdue: 'Overdue',
    websiteForm: 'Website form',
    websiteFormHelp: 'Your website can post enquiries to:',
  },
  billing: {
    title: 'Billing & Payments',
    subtitle: 'Invoices, payments, packages and insurance claims.',
    invoices: 'Invoices',
    payments: 'Payments',
    uninvoiced: 'To invoice',
    newInvoice: 'New invoice',
    editInvoice: 'Edit draft invoice',
    number: 'Invoice #',
    patient: 'Patient',
    issueDate: 'Issue date',
    dueDate: 'Due date',
    total: 'Total',
    paid: 'Paid',
    balance: 'Balance',
    status: 'Status',
    payment: 'Payment',
    all: 'All',
    outstanding: 'Outstanding',
    drafts: 'Drafts',
    items: 'Line items',
    description: 'Description',
    qty: 'Qty',
    unitPrice: 'Unit price',
    discount: 'Discount',
    tax: 'Tax %',
    amount: 'Amount',
    addService: 'Add service',
    addPackage: 'Add package',
    addCustom: 'Custom line',
    subtotal: 'Subtotal',
    taxTotal: 'Tax',
    grandTotal: 'Total',
    payer: 'Billed to',
    payerSelf: 'Patient',
    payerInsurance: 'Insurance',
    policy: 'Insurance policy',
    notes: 'Notes',
    saveDraft: 'Save draft',
    post: 'Post invoice',
    postConfirm: 'Post this invoice? It receives the next invoice number and can no longer be edited.',
    posted: 'Invoice {{number}} posted',
    deleteDraft: 'Delete draft',
    recordPayment: 'Record payment',
    method: 'Method',
    reference: 'Reference / UTR',
    receivedAt: 'Received on',
    paymentRecorded: 'Payment recorded',
    refund: 'Refund',
    refundReason: 'Reason for refund',
    refunded: 'Refund recorded',
    void: 'Void invoice',
    voidReason: 'Reason for voiding',
    voided: 'Invoice voided',
    creditNote: 'Credit note',
    creditAmount: 'Credit amount',
    creditReason: 'Reason',
    credited: 'Credit note issued',
    paymentLink: 'Payment link',
    linkCreated: 'Payment link created',
    downloadPdf: 'Download PDF',
    pdfIn: 'PDF in another language',
    printReceipt: 'Print receipt',
    receiptNo: 'Receipt #',
    claim: 'Insurance claim',
    claimStatus: 'Claim status',
    claimRef: 'Claim reference',
    claimStatuses: { submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', settled: 'Settled' },
    claimUpdated: 'Claim updated',
    history: 'Payment history',
    creditNotes: 'Credit notes',
    packages: 'Session packages',
    sessionsLeft: '{{remaining}} of {{total}} sessions left',
    expires: 'Expires {{date}}',
    noInvoices: 'No invoices yet',
    noPayments: 'No payments in this period',
    allInvoiced: 'All completed visits are invoiced',
    visit: 'Visit',
    createInvoice: 'Create invoice',
    invoiceCreated: 'Draft invoice created',
    issueBackdate: 'Issue date (for late posting)',
    voidedBecause: 'Voided: {{reason}}',
    receipt: 'Receipt',
  },
  reports: {
    title: 'Reports & Analytics',
    subtitle: 'Revenue, utilisation and outcomes for your clinic.',
    range: { '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last 12 months' },
    invoiced: 'Invoiced',
    collected: 'Collected',
    appointments: 'Appointments',
    completed: 'Completed',
    noShowRate: 'No-show rate',
    newPatients: 'New patients',
    revenueOverTime: 'Revenue over time',
    byService: 'Revenue by service',
    byPractitioner: 'Physiotherapist performance',
    aging: 'Outstanding by age',
    days: 'days',
    rating: 'Rating',
    delivered: 'Hours delivered',
    export: 'Export CSV',
    exportPatients: 'Export patients',
    exportInvoices: 'Export invoices',
    cancelled: 'Cancelled',
    noShows: 'No-shows',
    noData: 'No data for this period',
    feedback: 'Recent patient feedback',
  },
  staff: {
    title: 'Staff & Resources',
    subtitle: 'Team members, roles, rooms and working hours.',
    team: 'Team',
    rooms: 'Rooms',
    hours: 'Working hours',
    add: 'Add team member',
    edit: 'Edit team member',
    name: 'Full name',
    email: 'Work email',
    phone: 'Phone',
    jobTitle: 'Job title',
    specialization: 'Specialisation',
    role: 'Role',
    roles: { owner: 'Clinic owner', admin: 'Administrator', therapist: 'Physiotherapist', receptionist: 'Front desk', accountant: 'Accountant' },
    branches: 'Branches',
    practitioner: 'Sees patients (appears on the calendar)',
    active: 'Active',
    color: 'Calendar colour',
    created: 'Team member added. They will receive an email to set their password.',
    tempPassword: 'Temporary password (email is not configured): {{password}}',
    saved: 'Saved',
    deactivate: 'Deactivate',
    activate: 'Activate',
    lastLogin: 'Last sign-in',
    never: 'Never',
    addRoom: 'Add room',
    roomName: 'Room name',
    roomType: 'Type',
    roomTypes: { treatment: 'Treatment room', gym: 'Gym', hydro: 'Hydrotherapy', consult: 'Consultation' },
    selectPractitioner: 'Physiotherapist',
    addInterval: 'Add hours',
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    off: 'Off',
    hoursSaved: 'Working hours saved',
    directory: 'Directory',
    rolesTab: 'Roles & access',
    searchPlaceholder: 'Search by name, email, code or phone…',
    allRoles: 'All roles',
    allBranches: 'All branches',
    statusFilter: { active: 'Active', inactive: 'Inactive', all: 'All' },
    summary: { team: 'Team members', practitioners: '{{count}} physiotherapists', in: 'Clocked in today', late: '{{count}} late', leave: 'On leave today', pendingLeave: '{{count}} leave requests pending', notIn: 'Not in yet' },
    presence: { in: 'In since {{time}}', out: 'Left at {{time}}', leave: 'On leave', absent: 'Not in', late: 'Late' },
    today: 'Today',
    employee: 'Employee',
    contact: 'Contact',
    viewProfile: 'View profile',
    resetPassword: 'Reset password',
    signOutEverywhere: 'Sign out of all devices',
    signOutConfirm: '{{name}} will be signed out of the web portal and the mobile app.',
    signedOut: 'Signed out of all devices',
    noResults: 'No team members match these filters.',
    sections: { personal: 'Personal details', employment: 'Employment', access: 'Role & access', login: 'Sign-in', emergency: 'Emergency contact', scheduling: 'Scheduling' },
    employeeCode: 'Employee ID',
    employmentType: 'Employment type',
    employmentTypes: { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', visiting: 'Visiting consultant', intern: 'Intern' },
    joinedOn: 'Date of joining',
    dateOfBirth: 'Date of birth',
    gender: 'Gender',
    genders: { male: 'Male', female: 'Female', other: 'Other' },
    qualification: 'Qualification',
    registrationNumber: 'Council registration no.',
    address: 'Address',
    emergencyName: 'Contact name',
    emergencyPhone: 'Contact phone',
    hrNotes: 'HR notes (visible to managers only)',
    onlineBookable: 'Bookable on the online booking page',
    geofenceExempt: 'Can clock in from anywhere (home visits / field work)',
    credentials: { label: 'How will they sign in?', generate: 'Generate a temporary password', generateHint: 'Shown once after saving. Share it securely; they must change it at first sign-in.', password: 'Set a password now', passwordHint: 'They will be asked to change it at first sign-in.', invite: 'Email an invitation link', inviteHint: 'They set their own password from the email.' },
    password: 'Password',
    confirmPassword: 'Confirm password',
    passwordRules: 'At least 8 characters with letters and numbers.',
    loginDetails: 'Sign-in details',
    loginDetailsHint: 'Share these with {{name}}. The password is shown only once and must be changed at first sign-in.',
    copy: 'Copy',
    copied: 'Copied',
    copyAll: 'Copy sign-in details',
    shareWhatsApp: 'Send on WhatsApp',
    loginMessage: 'Hello {{name}}, your Physio360 account for {{clinic}} is ready.\nSign in: {{url}}\nEmail: {{email}}\nTemporary password: {{password}}\nYou will be asked to set a new password. The same login works in the Physio360 mobile app.',
    reset: { title: 'Reset password for {{name}}', generate: 'Generate a temporary password', set: 'Type a new password', email: 'Email a reset link to {{email}}', requireChange: 'Ask them to change it at next sign-in', done: 'Password updated. {{name}} has been signed out of all devices.', emailed: 'Reset link sent to {{email}}' },
    mustChange: 'Must change password',
    profile: {
      back: 'Team',
      tabs: { overview: 'Overview', performance: 'Performance', attendance: 'Attendance', leave: 'Leave', schedule: 'Schedule', access: 'Access & security' },
      memberSince: 'Joined {{date}}',
      tenure: '{{years}} yrs {{months}} mo',
      tenureMonths: '{{months}} months',
      appointments: 'Appointments',
      completed: 'Completed',
      completionRate: 'Completion rate',
      noShows: 'No-shows',
      cancelled: 'Cancelled',
      patientsSeen: 'Patients seen',
      revenue: 'Revenue billed',
      rating: 'Patient rating',
      ratingCount: 'from {{count}} reviews',
      noRating: 'No reviews yet',
      upcoming: '{{count}} booked in the next 7 days',
      presentDays: 'Days present',
      absentDays: 'Days absent',
      leaveDays: 'Leave taken',
      lateDays: 'Late arrivals',
      hoursWorked: 'Hours worked',
      unmarked: '{{count}} unmarked',
      month: 'Month',
      date: 'Date',
      checkIn: 'In',
      checkOut: 'Out',
      distance: '{{m}} m from clinic',
      leaveBalance: 'Leave balance {{year}}',
      remaining: '{{n}} left',
      unlimited: 'Unlimited',
      used: '{{used}} used · {{pending}} pending',
      role: 'Role',
      permissions: 'What this role can do',
      lastLogin: 'Last sign-in',
      passwordChanged: 'Password last changed',
      devices: 'Signed-in mobile devices',
      noDevices: 'Not signed in on any mobile device.',
      lastUsed: 'Last used {{date}}',
      activity: 'Recent activity',
      noActivity: 'No recorded activity yet.',
      deactivate: 'Deactivate account',
      reactivate: 'Reactivate account',
      deactivateConfirm: '{{name}} will no longer be able to sign in. Their records are kept.',
      notSet: 'Not set',
      workingHoursHint: 'Weekly hours drive the calendar and online booking slots.',
      editHours: 'Edit working hours',
    },
    roleMembers: '{{count}} active',
  },
  onlineBooking: {
    title: 'Online bookings',
    subtitle: 'Appointment requests from your booking link, website and WhatsApp.',
    shareLink: 'Share booking link',
    tabs: { pending: 'To confirm', confirmed: 'Confirmed', declined: 'Declined', all: 'All' },
    empty: 'No requests here.',
    emptyPending: 'No requests waiting. Share your booking link to start receiving appointments online.',
    requested: 'Requested {{when}}',
    preferred: 'Preferred slot',
    anyTherapist: 'Any available therapist',
    ageGender: '{{age}} yrs',
    confirm: 'Confirm',
    decline: 'Decline',
    declineTitle: 'Decline request from {{name}}',
    declineNote: 'Reason (kept internally)',
    declined: 'Request declined',
    confirmTitle: 'Confirm appointment for {{name}}',
    patientChoice: 'Patient record',
    newPatient: 'Register as a new patient',
    existingPatient: '{{name}} · {{mrn}}',
    matchesFound: 'A patient with this phone number already exists.',
    practitioner: 'Physiotherapist',
    time: 'Date & time',
    confirmed: 'Appointment booked for {{name}}',
    notifyWhatsApp: 'Send WhatsApp confirmation',
    confirmationText: 'Hello {{name}}, your appointment at {{clinic}} is confirmed for {{when}} ({{service}}). Reference {{ref}}. Reply here if you need to change it.',
    declineText: 'Hello {{name}}, thank you for your request to {{clinic}}. Unfortunately {{when}} is not available. Please reply with another convenient time or book again: {{url}}',
    viewPatient: 'Open patient',
    viewCalendar: 'Open in calendar',
    decidedBy: '{{status}} by {{name}} · {{when}}',
    sources: { link: 'Booking link', website: 'Website', whatsapp: 'WhatsApp', qr: 'QR code', instagram: 'Instagram', facebook: 'Facebook', google: 'Google' },
    share: {
      title: 'Share your booking page',
      subtitle: 'Patients pick a service, a therapist and a free slot. Requests land in Online bookings.',
      link: 'Booking link',
      open: 'Preview',
      whatsApp: 'Share on WhatsApp',
      whatsAppHint: 'Tip: set this link as your WhatsApp Business profile website and quick reply.',
      whatsAppText: 'Book your physiotherapy appointment at {{clinic}} online: {{url}}',
      embed: 'Embed on your website',
      copyEmbed: 'Copy embed code',
      iframeTitle: 'Book an appointment at {{clinic}}',
      button: '"Book appointment" button for your website',
      bookButton: 'Book an appointment',
      copyButton: 'Copy button code',
      qr: 'QR code',
      qrAlt: 'QR code for the booking page',
      qrHint: 'Print it at reception or on flyers.',
    },
    settings: {
      title: 'Online booking',
      subtitle: 'Let patients request appointments from your website, a link or WhatsApp.',
      enabled: 'Accept online bookings',
      mode: 'When a patient books',
      modes: { request: 'Front desk confirms', instant: 'Book instantly' },
      modeHelp: {
        request: 'Requests wait in Online bookings until your team confirms them. Recommended.',
        instant: 'The appointment is created immediately in a free slot and the patient record is registered automatically.',
      },
      leadMinutes: 'Minimum notice (minutes)',
      leadHint: 'Slots sooner than this are not offered.',
      daysAhead: 'Bookable days ahead',
      choosePractitioner: 'Let patients choose their physiotherapist',
      intro: 'Welcome message',
      introHint: 'Shown at the top of the booking page.',
      servicesHint: 'Choose which services and physiotherapists appear online from Services & pricing and each team member\'s profile.',
    },
  },
  invoiceSettings: {
    letterhead: 'Letterhead',
    letterheadHelp: 'Your logo appears on invoices, receipts, prescriptions and the online booking page.',
    logo: 'Clinic logo',
    uploadLogo: 'Upload logo',
    replaceLogo: 'Replace logo',
    logoHint: 'PNG or JPG, at least 64 px, up to 2 MB. A transparent PNG looks best.',
    logoSaved: 'Logo updated',
    title: 'Invoice details',
    subtitle: 'Printed on every invoice. Leave blank to hide.',
    upi: 'UPI ID',
    upiHint: 'Adds a scan-to-pay QR for the balance on Indian invoices.',
    signatory: 'Authorised signatory',
    signatoryPlaceholder: 'Dr. … (Clinic Director)',
    bank: 'Bank details',
    terms: 'Terms & conditions',
    footer: 'Thank-you note',
    footerPlaceholder: 'Thank you for choosing us!',
    accent: 'Accent colour',
  },
  settings: {
    title: 'Settings',
    booking: 'Online booking',
    invoice: 'Invoice & letterhead',
    geofence: 'Clock-in location',
    noLocation: 'Not set',
    geofenceBranchHelp: 'Staff can clock in only within this radius when geofencing is on (Settings → HR).',
    latitude: 'Latitude',
    longitude: 'Longitude',
    radius: 'Radius (m)',
    useMyLocation: 'Use my current location',
    viewOnMap: 'View on map',
    noGeolocation: 'Could not read this device\'s location.',
    geofenceRequired: 'Geofenced clock-in',
    geofenceHelp: 'Staff must be at a branch to clock in or out from the web or mobile app. Field staff can be exempted on their profile.',
    setBranchLocations: 'Set branch locations',
    serviceDescription: 'Description',
    serviceDescriptionHint: 'Shown to patients on the booking page.',
    onlineBookable: 'Bookable online',
    online: 'Online',
    onlineYes: 'Online',
    subtitle: 'Clinic profile, services, pricing and integrations.',
    clinic: 'Clinic profile',
    branches: 'Branches',
    services: 'Services & pricing',
    packages: 'Session packages',
    payers: 'Insurance & TPA',
    consents: 'Consent forms',
    payments: 'Online payments',
    portal: 'Patient app',
    clinicName: 'Clinic name',
    legalName: 'Legal name',
    taxNumber: 'GSTIN / VAT number',
    address: 'Address',
    timezone: 'Timezone',
    currency: 'Currency',
    defaultLanguage: 'Default language',
    saved: 'Settings saved',
    addBranch: 'Add branch',
    branchName: 'Branch name',
    branchCode: 'Code (used in invoice numbers)',
    city: 'City',
    addService: 'Add service',
    serviceName: 'Service name',
    category: 'Category',
    duration: 'Duration (min)',
    price: 'Price',
    taxRate: 'Tax %',
    colour: 'Colour',
    active: 'Active',
    addPackage: 'Add package',
    packageName: 'Package name',
    sessions: 'Sessions',
    validity: 'Valid for (days)',
    forService: 'For service',
    anyService: 'Any service',
    addPayer: 'Add insurer / TPA',
    payerName: 'Name',
    payerType: 'Type',
    payerTypes: { insurer: 'Insurer', tpa: 'TPA', corporate: 'Corporate' },
    addConsent: 'Add consent form',
    consentTitle: 'Title',
    consentBody: 'Text shown to the patient',
    required: 'Required before treatment',
    version: 'v{{version}}',
    versionNote: 'Editing the text creates a new version; patients will be asked to sign again.',
    razorpay: 'Razorpay',
    razorpayHelp: 'Patients can pay invoices online through Razorpay payment links. Secrets are stored encrypted and never shown again.',
    keyId: 'Key ID',
    keySecret: 'Key secret',
    webhookSecret: 'Webhook secret',
    webhookUrl: 'Webhook URL to configure in Razorpay',
    connected: 'Connected',
    notConnected: 'Not connected',
    portalBooking: 'Allow online booking from the patient app',
    leadTime: 'Minimum notice for online bookings (minutes)',
    cancelWindow: 'Online cancellation allowed until (hours before)',
    reminderHours: 'Send reminders (hours before)',
    messaging: 'Messaging',
    hr: 'Attendance & leave',
    twilio: 'Twilio (SMS & WhatsApp)',
    twilioHelp: 'Connect your own Twilio account to send SMS and WhatsApp messages automatically. Without it, messages open WhatsApp or the SMS app with the text ready to send. Credentials are stored encrypted and never shown again.',
    accountSid: 'Account SID',
    authToken: 'Auth token',
    smsFrom: 'SMS sender (number or sender ID)',
    whatsappFrom: 'WhatsApp sender number',
    disconnect: 'Disconnect',
    channelStatus: { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' },
    weeklyOff: 'Weekly off days',
    holidays: 'Clinic holidays',
    holidayName: 'Holiday',
    addHoliday: 'Add holiday',
    noHolidays: 'No holidays added',
    graceMinutes: 'Mark late after (minutes past shift start)',
    hrHelp: 'Weekly offs and holidays are excluded from leave days, the attendance register and payroll working days.',
    leaveTypes: 'Leave types',
    templates: 'Message templates',
  },
  audit: {
    title: 'Audit Log',
    subtitle: 'Every change to records, logins and exports (append-only).',
    when: 'When',
    who: 'Who',
    event: 'Event',
    record: 'Record',
    changes: 'Changes',
    system: 'System',
    empty: 'No audit entries',
  },
  profile: {
    title: 'My profile',
    details: 'Personal details',
    security: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordChanged: 'Password changed. Other devices have been signed out.',
    language: 'Interface language',
    appearance: 'Appearance',
  },
  consultations: {
    title: 'Consultations',
    subtitle: 'Initial and follow-up assessments across the clinic.',
    date: 'Date',
    patient: 'Patient',
    practitioner: 'Physiotherapist',
    complaint: 'Chief complaint',
    diagnosis: 'Diagnosis',
    status: 'Status',
    prescription: 'Prescription',
    issued: 'Issued',
    notIssued: 'Not issued',
    writePrescription: 'Write prescription',
    printPrescription: 'Print prescription',
    openRecord: 'Open record',
    newConsultation: 'New consultation',
    newConsultationHint: 'Open the patient record and add an assessment.',
    summary: { thisMonth: 'Consultations this month', unsigned: 'Awaiting signature', withoutRx: 'Without prescription (this month)' },
    filters: { all: 'All', draft: 'Draft', signed: 'Signed' },
    empty: 'No consultations found',
    emptyHint: 'Assessments recorded on patient records appear here.',
  },
  prescriptions: {
    title: 'Prescriptions',
    subtitle: 'Physiotherapy prescriptions issued to patients, ready to print.',
    new: 'New prescription',
    edit: 'Edit prescription',
    number: 'Rx #',
    issuedOn: 'Issued on',
    reviewOn: 'Review on',
    diagnosis: 'Diagnosis',
    basedOn: 'Based on assessment',
    plan: 'Copy home exercises from plan',
    none: 'None',
    modalities: 'Treatment (modalities & techniques)',
    modality: 'Modality / technique',
    parameters: 'Parameters',
    minutes: 'Minutes',
    sessions: 'Sessions',
    addModality: 'Add treatment',
    exercises: 'Home exercises',
    exercise: 'Exercise',
    dosage: 'Dosage',
    exerciseNotes: 'Notes',
    addExercise: 'Add exercise',
    advice: 'Advice',
    precautions: 'Precautions',
    print: 'Print',
    printIn: 'Print in',
    created: 'Prescription {{number}} issued',
    saved: 'Prescription saved',
    empty: 'No prescriptions yet',
    emptyHint: 'Write a prescription from a consultation or the patient record.',
    onlyAuthor: 'Only the prescribing physiotherapist can edit this prescription.',
    remove: 'Remove',
    items: '{{modalities}} treatments · {{exercises}} exercises',
  },
  timeline: {
    title: 'Patient timeline',
    show: 'Show',
    all: 'Everything',
    empty: 'Nothing recorded yet',
    types: {
      registered: 'Registered',
      appointment: 'Appointment',
      assessment: 'Assessment',
      note: 'Progress note',
      plan: 'Treatment plan',
      prescription: 'Prescription',
      progress: 'Outcome recorded',
      document: 'Document',
      consent: 'Consent',
      invoice: 'Invoice',
      payment: 'Payment',
      message: 'Message',
    },
    text: {
      registered: 'Registered as {{mrn}}',
      appointment: '{{service}} with {{practitioner}}',
      note: 'Note by {{author}}',
      plan: '{{title}} · {{weeks}} weeks',
      prescription: '{{number}} by {{practitioner}}',
      consent: '{{title}} · signed by {{signer}}',
      invoice: '{{number}} · {{amount}}',
      payment: '{{amount}} · {{method}}',
      refund: 'Refund {{amount}} · {{method}}',
      message: '{{channel}} · {{purpose}}',
      pain: 'Pain {{value}}/10',
      function: 'Function {{value}}/100',
    },
  },
  packages: {
    title: 'Packages',
    subtitle: 'Session packages sold to patients: balance, usage and expiry.',
    sell: 'Sell a package',
    plans: 'Package plans',
    patient: 'Patient',
    package: 'Package',
    sessions: 'Sessions',
    remaining: '{{remaining}} of {{total}} left',
    purchased: 'Purchased',
    expires: 'Expires',
    noExpiry: 'No expiry',
    expiringSoon: 'Expiring soon',
    summary: { active: 'Active packages', expiring: 'Expiring in 14 days', sessions: 'Sessions remaining', sold: 'Sold (30 days)' },
    filters: { all: 'All', active: 'Active', expiring: 'Expiring soon', exhausted: 'Used up', expired: 'Expired' },
    empty: 'No packages',
    emptyHint: 'Add a package to an invoice to sell it to a patient.',
  },
  messages: {
    title: 'Messages',
    subtitle: 'SMS, WhatsApp and email to patients, with a complete log.',
    compose: 'New message',
    log: 'Message log',
    templates: 'Templates',
    channel: 'Channel',
    channels: { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' },
    purpose: 'Purpose',
    purposes: { appointment_reminder: 'Appointment reminder', payment_reminder: 'Payment reminder', follow_up: 'Follow-up', birthday: 'Birthday', general: 'General' },
    recipients: 'Patients',
    addPatient: 'Add patient',
    template: 'Template',
    noTemplate: 'Write my own',
    subject: 'Subject',
    body: 'Message',
    insertField: 'Insert field',
    preview: 'Preview',
    previewFor: 'Preview for {{name}}',
    characters: '{{count}} characters',
    send: 'Send',
    sendCount: 'Send ({{count}})',
    queued: 'Messages created: {{count}}',
    manualReady: 'Ready to send from your phone',
    manualHint: 'No gateway is connected for this channel. Open each message to send it from the clinic phone.',
    openWhatsApp: 'Open WhatsApp',
    openSms: 'Open SMS app',
    skipped: 'Skipped {{count}}',
    skipReasons: { no_recipient: 'No contact for this channel', not_found: 'Patient not found', nothing_due: 'Nothing due', channel_not_configured: 'Channel not connected' },
    to: 'To',
    when: 'When',
    sentBy: 'Sent by',
    automatic: 'Automatic',
    status: 'Status',
    allChannels: 'All channels',
    allStatuses: 'All statuses',
    allPurposes: 'All purposes',
    empty: 'No messages yet',
    emptyHint: 'Messages you send to patients will be logged here.',
    summary: { sent: 'Sent (30 days)', manual: 'Sent manually (30 days)', failed: 'Failed (30 days)', queued: 'Queued' },
    gatewayMissing: 'SMS and WhatsApp are not connected. Messages open WhatsApp or the SMS app with the text ready, and are still logged.',
    connect: 'Connect in Settings',
    templateName: 'Template name',
    newTemplate: 'New template',
    editTemplate: 'Edit template',
    isDefault: 'Default for this purpose and language',
    language: 'Language',
    templateSaved: 'Template saved',
    adminOnly: 'Only clinic administrators can change templates.',
    default: 'Default',
  },
  reminders: {
    title: 'Payment Reminders',
    subtitle: 'Outstanding invoices and reminders to patients.',
    invoice: 'Invoice',
    patient: 'Patient',
    due: 'Due date',
    overdue: '{{count}} days overdue',
    notDue: 'Not yet due',
    balance: 'Balance',
    reminded: 'Last reminded',
    never: 'Never',
    times: '× {{count}}',
    selectAll: 'Select all',
    select: 'Select {{number}}',
    remindSelected: 'Remind selected ({{count}})',
    remind: 'Remind',
    channel: 'Send via',
    includeLink: 'Include an online payment link',
    linkUnavailable: 'Online payments are not set up',
    created: 'Reminders created: {{count}}',
    filters: { all: 'All outstanding', due: 'Overdue', '7': '7+ days', '30': '30+ days', '60': '60+ days' },
    summary: { count: 'Outstanding invoices', amount: 'Amount due', overdue: 'Overdue invoices' },
    automation: {
      title: 'Automatic reminders',
      enabled: 'Send reminders automatically',
      description: 'Reminders are sent at 10:00 clinic time on the chosen days after the due date, once per day per invoice.',
      days: 'Days after the due date',
      save: 'Save',
      saved: 'Automatic reminders updated',
      needsGateway: 'Automatic SMS and WhatsApp need a connected gateway. Email always works.',
    },
    noContact: 'No contact',
    empty: 'Nothing outstanding',
    emptyHint: 'All posted invoices are fully paid.',
  },
  attendance: {
    title: 'Attendance',
    subtitle: 'Daily clock-in, the monthly register and corrections.',
    myDay: 'My day',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    clockedIn: 'Clocked in at {{time}}',
    clockedOut: 'Clocked out at {{time}}',
    notClockedIn: 'Not clocked in yet',
    worked: 'Worked {{time}}',
    late: 'Late',
    onLeave: 'On leave today',
    onHalfLeave: 'Half-day leave today',
    register: 'Monthly register',
    staffMember: 'Staff member',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    summary: { present: 'Present', absent: 'Absent', leave: 'Leave', late: 'Late', hours: 'Hours', unmarked: 'Unmarked' },
    legend: { present: 'Present', half_day: 'Half day', absent: 'Absent', leave: 'Leave', holiday: 'Holiday', off: 'Weekly off', unmarked: 'Not marked' },
    record: 'Record attendance',
    recordFor: '{{name}} · {{date}}',
    status: 'Status',
    checkIn: 'Clock-in',
    checkOut: 'Clock-out',
    notes: 'Notes',
    saved: 'Attendance saved',
    hours: '{{h}}h {{m}}m',
    clickToEdit: 'Select a day to record or correct it.',
    lateCount: '{{count}} late',
    geofenceHint: 'Clock-in uses your location. Allow location access when asked.',
  },
  leave: {
    title: 'Leave',
    subtitle: 'Requests, approvals and balances.',
    apply: 'Apply for leave',
    applyFor: 'Staff member',
    myRequests: 'My requests',
    approvals: 'Team requests',
    type: 'Leave type',
    from: 'From',
    to: 'To',
    dayType: 'Duration',
    halfDays: { none: 'Full day(s)', am: 'Morning (first half)', pm: 'Afternoon (second half)' },
    days: 'Days',
    reason: 'Reason',
    status: 'Status',
    statuses: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled' },
    approve: 'Approve',
    reject: 'Reject',
    cancelRequest: 'Cancel request',
    cancelConfirm: 'Cancel this leave request?',
    rejectReason: 'Reason for rejecting',
    note: 'Note (optional)',
    decided: '{{name}} · {{date}}',
    balances: 'Leave balance {{year}}',
    quota: 'Quota',
    used: 'Used',
    pending: 'Pending',
    remaining: 'Remaining',
    unlimited: 'No limit',
    unpaid: 'Unpaid',
    submitted: 'Leave request submitted',
    approved: 'Leave approved',
    rejected: 'Leave rejected',
    cancelled: 'Leave cancelled',
    conflicts: '{{count}} booked appointment(s) in this period',
    conflictsHint: 'Reschedule these patients before or after approving.',
    awaiting: '{{count}} awaiting approval',
    empty: 'No leave requests',
    emptyHint: 'Requests you submit will appear here.',
    year: 'Year',
    staffMember: 'Staff member',
    typeName: 'Name',
    code: 'Code',
    annualQuota: 'Days per year (0 = no limit)',
    paid: 'Paid leave',
    addType: 'Add leave type',
    editType: 'Edit leave type',
    typeSaved: 'Leave type saved',
  },
  payroll: {
    title: 'Payroll',
    subtitle: 'Salaries, monthly payroll runs and payslips.',
    runs: 'Payroll runs',
    salaries: 'Salary structures',
    myPayslips: 'My payslips',
    newRun: 'Run payroll',
    period: 'Month',
    runCreated: 'Payroll for {{period}} created',
    status: 'Status',
    workingDays: 'Working days',
    employees: 'Employees',
    gross: 'Gross',
    deductions: 'Deductions',
    net: 'Net pay',
    totalNet: 'Total net pay',
    open: 'Open',
    back: 'All payroll runs',
    finalize: 'Finalize payroll',
    finalizeConfirm: 'Finalize this payroll? Payslips become final, visible to staff, and can no longer be edited.',
    finalized: 'Payroll finalized',
    markPaid: 'Mark as paid',
    markPaidConfirm: 'Confirm that salaries for this month have been paid?',
    paid: 'Payroll marked as paid',
    recalculate: 'Recalculate',
    recalculateConfirm: 'Rebuild all payslips from current salaries, leave and attendance? Manual adjustments will be lost.',
    recalculated: 'Payslips recalculated',
    deleteRun: 'Delete draft',
    deleteConfirm: 'Delete this draft payroll?',
    deleted: 'Draft payroll deleted',
    employee: 'Employee',
    paidDays: 'Paid days',
    lopDays: 'LOP days',
    lopHint: 'Loss of pay = unpaid leave + days marked absent.',
    earnings: 'Earnings',
    addEarning: 'Add earning',
    addDeduction: 'Add deduction',
    lineName: 'Description',
    amount: 'Amount',
    adjust: 'Adjust',
    adjustTitle: 'Adjust payslip · {{name}}',
    payslipNotes: 'Note on payslip',
    adjusted: 'Payslip updated',
    payslip: 'Payslip',
    download: 'Download payslip',
    missingSalary: 'Not included (no salary structure): {{names}}',
    setSalary: 'Set salary',
    editSalary: 'Edit salary · {{name}}',
    basic: 'Basic salary (monthly)',
    allowances: 'Allowances',
    allowance: 'Allowance',
    fixedDeductions: 'Fixed deductions',
    deduction: 'Deduction',
    effectiveFrom: 'Effective from',
    bankAccount: 'Bank account',
    taxId: 'PAN / tax ID',
    monthlyGross: 'Monthly gross',
    monthlyNet: 'Monthly net',
    salarySaved: 'Salary saved',
    notSet: 'Not set',
    noRuns: 'No payroll runs yet',
    noRunsHint: 'Set salary structures, then run payroll for a month.',
    noPayslips: 'No payslips yet',
    noPayslipsHint: 'Your payslips appear here once payroll is finalized.',
    remove: 'Remove',
  },
  support: {
    title: 'Help & Support',
    subtitle: 'Get help from the SYSNAC support team.',
    newTicket: 'New support request',
    subject: 'Subject',
    category: 'Category',
    categories: { question: 'Question / how-to', bug: 'Something is not working', billing: 'Subscription & billing', feature: 'Feature request', training: 'Training', data: 'Data import / export' },
    priority: 'Priority',
    priorities: { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent — the clinic cannot work' },
    description: 'Describe the issue',
    descriptionHint: 'What you were doing, what happened and what you expected. Do not include patient health information.',
    submit: 'Submit request',
    created: 'Request {{number}} submitted',
    replyPlaceholder: 'Write a reply…',
    sendReply: 'Send reply',
    replySent: 'Reply sent',
    markResolved: 'Mark resolved',
    closeTicket: 'Close request',
    reopen: 'Reopen',
    updated: 'Request updated',
    lastUpdate: 'Last update',
    openedBy: 'Opened by',
    sysnac: 'SYSNAC Support',
    filters: { open: 'Open', closed: 'Closed', all: 'All' },
    empty: 'No support requests',
    emptyHint: 'Raise a request and our team will reply here and by email.',
    contact: 'Contact SYSNAC',
    email: 'Email',
    phone: 'Phone',
    whatsapp: 'WhatsApp',
    hours: 'Support hours',
    guide: 'User guide',
    quickHelp: 'Quick help',
    faq: {
      q1: 'How do I add a new branch?',
      a1: 'Settings → Branches → Add branch. Then give physiotherapists working hours there so it can be booked.',
      q2: 'How do I send payment reminders?',
      a2: 'Billing & Finance → Payment Reminders. Select invoices and send by WhatsApp, SMS or email, or switch on automatic reminders.',
      q3: 'How do I print a prescription?',
      a3: 'Open Consultations or the patient’s Prescriptions tab, write the prescription and choose Print. It prints in English, Arabic or Malayalam.',
      q4: 'Why can’t a patient book a physiotherapist?',
      a4: 'Check their working hours and approved leave. Approved leave removes those hours from booking.',
    },
    closedNote: 'This request is closed. Open a new request if you need more help.',
  },
  errorCodes: {
    practitioner_on_leave: 'The physiotherapist is on approved leave at that time.',
    outside_working_hours: 'That time is outside the physiotherapist’s working hours at this branch.',
    insufficient_balance: 'Not enough leave balance for these dates.',
    overlapping_leave: 'This overlaps another leave request.',
    no_working_days: 'The selected dates are not working days.',
    invalid_half_day: 'Half-day leave must be a single date.',
    self_approval: 'You cannot decide your own leave request.',
    already_checked_in: 'You have already clocked in today.',
    already_checked_out: 'You have already clocked out today.',
    not_checked_in: 'Clock in before clocking out.',
    future_date: 'Attendance cannot be recorded for a future date.',
    payroll_exists: 'Payroll for this month already exists.',
    payroll_finalized: 'This payroll is finalized and can no longer change.',
    empty_payroll: 'There are no payslips to finalize. Set salary structures first.',
    no_recipient: 'The patient has no contact for this channel.',
    ticket_closed: 'This request is closed. Open a new request instead.',
    nothing_due: 'This invoice has nothing due.',
  },
  notFound: { title: 'Page not found', text: 'The page you are looking for does not exist.', home: 'Go to dashboard' },
};
